import { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { ApiError, type LojaOuro, type PagamentoOuro } from '../api/types';
import { existe, idiomaAtual, numero, t } from '../i18n/idioma';
import { PILE_OURO } from '../assets/items';
import { abrirJanelaCheckout, fecharJanela, levarAoCheckout, LinkDoCheckout, motivoDaRecusa } from './LojaCartao';

const reais = (c: number) => (c / 100).toLocaleString(idiomaAtual(), { style: 'currency', currency: 'BRL' });

/** O código do servidor vira texto no idioma do jogador; sem tradução, a mensagem do servidor. */
function mensagem(e: unknown): string {
  if (e instanceof ApiError) {
    const chave = e.code ? `pag.erro.${e.code}` : '';
    return chave && existe(chave) ? t(chave) : e.message;
  }
  return t('pag.erro.generico');
}

const novaChave = () =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
    });

const ABERTOS = new Set(['criando', 'aguardando', 'processando']);

/**
 * A aba Ouro (techspec-pagamentos-mercado-pago §07.1). Os pacotes, o personagem que recebe e o aceite da
 * política vêm do servidor; o valor nunca sai daqui (o servidor copia do pacote). O ouro entra no cofre
 * só depois que o servidor consulta o pagamento no Mercado Pago.
 */
export function AbaOuro({ nome, shard, onPago }: { nome: string; shard: string; onPago(ouro: number): void }) {
  const [info, setInfo] = useState<LojaOuro | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [aceite, setAceite] = useState(false);
  const [politica, setPolitica] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pedido, setPedido] = useState<PagamentoOuro | null>(null);
  const [metodo, setMetodo] = useState<'pix' | 'cartao'>('pix');
  /** O navegador bloqueou a janela do Mercado Pago: o modal mostra o link em destaque. */
  const [bloqueada, setBloqueada] = useState(false);
  const chave = useRef(novaChave());

  const carregar = () => {
    void api.lojaOuro().then(v => { setInfo(v); setErro(null); }).catch(e => setErro(mensagem(e)));
  };
  useEffect(carregar, []);

  if (!info) return <p className="muted">{erro ?? t('comum.carregando')}</p>;

  const pacotes = info.pacotes;
  const pac = pacotes.find(p => p.id === sel) ?? pacotes[1] ?? pacotes[0] ?? null;
  if (!info.vendaAberta || !pac) {
    const motivo = info.codigo && existe(`pag.erro.${info.codigo}`) ? t(`pag.erro.${info.codigo}`) : info.motivo;
    return (
      <div className="lj-breve">
        <img src={PILE_OURO} alt="" />
        <h3>{info.bloqueio ? t('pag.bloqueada') : t('loja.ouroEmBreve')}</h3>
        <p>{motivo ?? t('loja.ouroEmBreveDica')}</p>
      </div>
    );
  }

  const destino = info.personagem?.nome ?? nome;
  const temPix = info.metodos.includes('pix');
  const temCartao = info.metodos.includes('cartao');
  const forma: 'pix' | 'cartao' = metodo === 'cartao' && temCartao ? 'cartao' : temPix ? 'pix' : 'cartao';
  const pendentes = info.pendentes;
  const livre = Math.max(0, info.semana.limiteOuro - info.semana.usadoOuro);
  const passa = pac.ouro > livre;

  const pagar = async () => {
    if (!aceite || busy) return;
    // Cartão (Checkout Pro): a janela abre já no clique; o endereço do Mercado Pago chega com o pedido.
    const janela = forma === 'cartao' ? abrirJanelaCheckout() : null;
    setBusy(true);
    setErro(null);
    setBloqueada(false);
    try {
      const p = await api.pedirOuro({
        pacoteId: pac.id,
        characterId: info.personagem?.id ?? null,
        metodo: forma,
        chave: chave.current,
        politicaVersao: info.politica.versao,
        aceite
      });
      setPedido(p);
      if (forma === 'cartao') {
        if (p.estado === 'aguardando' && p.checkout) setBloqueada(!levarAoCheckout(janela, p.checkout));
        else fecharJanela(janela);
      }
      // A chave só se renova quando o pedido abriu: repetir depois de erro reaproveita a mesma (sem cobrança dupla).
      if (p.estado !== 'falhou') chave.current = novaChave();
    } catch (e) {
      fecharJanela(janela);
      setErro(mensagem(e));
      if (e instanceof ApiError && e.status === 409) chave.current = novaChave();
    } finally {
      setBusy(false);
    }
  };

  const abrirPendente = async (id: string) => {
    setErro(null);
    try { setPedido(await api.pedidoOuro(id)); } catch (e) { setErro(mensagem(e)); }
  };

  return (
    <>
      {pendentes.length > 0 && !pedido && (
        <p className="notice lj-pend" role="status">
          {t('pag.pendente', { ouro: numero(pendentes[0].ouro) })}
          <button type="button" className="btn btn--tiny" onClick={() => void abrirPendente(pendentes[0].id)}>{t('pag.abrir')}</button>
        </p>
      )}
      <div className="lj-ouro-g">
        <div className="lj-pacs">
          {pacotes.map((p, i) => (
            <button key={p.id} type="button" className="lj-pac" aria-pressed={pac.id === p.id} onClick={() => setSel(p.id)}>
              <span className="lj-pac__pilha"><img src={PILE_OURO} alt="" style={{ height: 26 + Math.min(i, 4) * 9 }} /></span>
              <b>{numero(p.ouro)}<small>{t('loja.ouro')}</small></b>
              <span>{reais(p.centavos)}</span>
              <em>{t('loja.por100', { v: reais(Math.round((p.centavos / p.ouro) * 100)) })}</em>
            </button>
          ))}
        </div>
        <div className="lj-pedido">
          <h4>{t('loja.seuPedido')}</h4>
          {info.simulado
            ? <span className="lj-teste lj-teste--sim">{t('pag.simulado.chip')}</span>
            : info.ambiente === 'teste' && <span className="lj-teste">{t('pag.teste')}</span>}
          <div className="lj-linha"><span>{t('loja.ouroCap')}</span><b>{numero(pac.ouro)}</b></div>
          <div className="lj-linha"><span>{t('loja.vaiPara')}</span><b>{destino}{shard ? ` · ${shard}` : ''}</b></div>
          <div className="lj-linha"><span>{t('pag.semana')}</span><b>{t('pag.semanaN', { usado: numero(info.semana.usadoOuro), limite: numero(info.semana.limiteOuro) })}</b></div>
          <div className="lj-linha"><span>{t('loja.total')}</span><b>{reais(pac.centavos)}</b></div>
          <div className="lj-met" role="radiogroup" aria-label={t('pag.forma')}>
            {temPix && <button type="button" role="radio" aria-checked={forma === 'pix'} aria-pressed={forma === 'pix'} onClick={() => setMetodo('pix')}>Pix</button>}
            {temCartao && <button type="button" role="radio" aria-checked={forma === 'cartao'} aria-pressed={forma === 'cartao'} onClick={() => setMetodo('cartao')}>{t('pag.cartao')}</button>}
          </div>
          {forma === 'cartao' && <p className="lj-aviso">{t('pag.checkout.nota')}</p>}
          {forma === 'cartao' && (info.parcelasMax ?? 1) > 1 && <p className="lj-aviso">{t('pag.parcelasAte', { n: info.parcelasMax ?? 1 })}</p>}
          <label className="lj-aceite">
            <input type="checkbox" checked={aceite} onChange={e => setAceite(e.target.checked)} />
            <span>
              {t('pag.aceite')}{' '}
              <button type="button" className="lj-link" onClick={() => setPolitica(v => !v)}>{t('pag.verPolitica')}</button>
            </span>
          </label>
          {politica && <div className="lj-politica" tabIndex={0}>{info.politica.texto || t('pag.semPolitica')}</div>}
          {erro && <p className="notice" role="alert">{erro}</p>}
          <button type="button" className="btn btn--primary" disabled={!aceite || busy || passa} onClick={() => void pagar()}>
            {busy ? t('pag.abrindo') : t('loja.pagar', { v: reais(pac.centavos) })}
          </button>
          {passa && <p className="lj-aviso">{t('pag.erro.LimiteSemanal')}</p>}
          {info.aviso18 && <p className="lj-aviso">{info.aviso18}</p>}
          <p className="lj-aviso">{t('pag.naoTransfere', { nome: destino })}</p>
        </div>
      </div>
      {pedido && (
        <PedidoModal
          pedido={pedido}
          destino={destino}
          consultaS={info.pixConsultaS}
          simulado={!!info.simulado}
          bloqueada={bloqueada}
          onTroca={setPedido}
          onFechar={() => { setPedido(null); setBloqueada(false); carregar(); }}
          onPago={onPago}
        />
      )}
    </>
  );
}

/**
 * O modal do pedido. Pix (§07.2): QR em base64 do servidor, copia e cola, contador, consulta e cancelar. Cartão
 * (techspec-checkout-pro): o jogador paga na janela do Mercado Pago, e o modal acompanha o pedido até o resultado.
 */
function PedidoModal({ pedido, destino, consultaS, simulado, bloqueada, onTroca, onFechar, onPago }: {
  pedido: PagamentoOuro;
  destino: string;
  consultaS: number;
  simulado: boolean;
  bloqueada: boolean;
  onTroca(p: PagamentoOuro): void;
  onFechar(): void;
  onPago(ouro: number): void;
}) {
  const [agora, setAgora] = useState(() => Date.now());
  const [copiou, setCopiou] = useState(false);
  const [confirmarCancelar, setConfirmarCancelar] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const avisou = useRef(false);
  const caixa = useRef<HTMLDivElement>(null);
  const aberto = ABERTOS.has(pedido.estado);

  useEffect(() => { caixa.current?.querySelector<HTMLElement>('button')?.focus(); }, []);

  useEffect(() => {
    if (!aberto) return;
    const relogio = window.setInterval(() => setAgora(Date.now()), 1000);
    const consulta = window.setInterval(() => {
      void api.pedidoOuro(pedido.id).then(onTroca).catch(() => { /* a próxima tenta de novo */ });
    }, Math.max(3, consultaS) * 1000);
    return () => { window.clearInterval(relogio); window.clearInterval(consulta); };
  }, [aberto, pedido.id, consultaS]);

  useEffect(() => {
    if (pedido.estado === 'pago' && !avisou.current) {
      avisou.current = true;
      onPago(pedido.ouro);
    }
  }, [pedido.estado]);

  const expira = pedido.expiraEm ? Date.parse(pedido.expiraEm) : NaN;
  const resta = Number.isNaN(expira) ? null : Math.max(0, Math.floor((expira - agora) / 1000));
  const relogio = resta == null ? '' : `${Math.floor(resta / 60)}:${String(resta % 60).padStart(2, '0')}`;

  const copiar = async () => {
    if (!pedido.pixCopiaCola) return;
    try {
      await navigator.clipboard.writeText(pedido.pixCopiaCola);
      setCopiou(true);
      window.setTimeout(() => setCopiou(false), 2000);
    } catch {
      setErro(t('pag.naoCopiou'));
    }
  };

  const cancelar = async () => {
    setBusy(true);
    setErro(null);
    try { onTroca(await api.cancelarOuro(pedido.id)); } catch (e) { setErro(mensagem(e)); } finally {
      setBusy(false);
      setConfirmarCancelar(false);
    }
  };

  let miolo: React.ReactNode;
  if (pedido.estado === 'pago') {
    miolo = (
      <>
        <h3>{t('pag.pago')}</h3>
        <div className="lj-caixa__arte"><img src={PILE_OURO} alt="" /></div>
        <p>{t('pag.pagoDica', { ouro: numero(pedido.ouro), nome: pedido.personagem ?? destino })}</p>
        <button type="button" className="btn btn--primary" onClick={onFechar}>{t('comum.fechar')}</button>
      </>
    );
  } else if (aberto && pedido.metodo === 'cartao') {
    miolo = (
      <>
        <h3>{t('pag.checkout.titulo', { v: reais(pedido.centavos) })}</h3>
        {simulado
          ? <p className="pag-simulado"><b>{t('pag.simulado.chip')}</b> {t('pag.checkout.simulado')}</p>
          : pedido.ambiente === 'teste' && <span className="lj-teste">{t('pag.teste')}</span>}
        <p>{bloqueada ? t('pag.checkout.bloqueada') : t('pag.checkout.dica')}</p>
        <LinkDoCheckout url={pedido.checkout} classe={bloqueada ? 'btn btn--primary' : 'btn btn--ghost'} />
        <p className="lj-espera"><i />{t('pag.checkout.aguardando')}{relogio ? ` · ${relogio}` : ''}</p>
        <p className="lj-aviso">{t('pag.checkout.seguro')}</p>
        {erro && <p className="notice" role="alert">{erro}</p>}
        {confirmarCancelar ? (
          <div className="lj-caixa__acoes">
            <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => setConfirmarCancelar(false)}>{t('pag.manter')}</button>
            <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void cancelar()}>{t('pag.simCancelar')}</button>
          </div>
        ) : (
          <div className="lj-caixa__acoes">
            <button type="button" className="btn btn--ghost" disabled={busy || pedido.estado !== 'aguardando'} onClick={() => setConfirmarCancelar(true)}>{t('pag.checkout.desistir')}</button>
            <button type="button" className="btn btn--primary" onClick={onFechar}>{t('comum.fechar')}</button>
          </div>
        )}
      </>
    );
  } else if (aberto) {
    miolo = (
      <>
        <h3>{t('pag.pixTitulo', { v: reais(pedido.centavos) })}</h3>
        {simulado
          ? <p className="pag-simulado"><b>{t('pag.simulado.chip')}</b> {t('pag.simulado.pix')}</p>
          : pedido.ambiente === 'teste' && <span className="lj-teste">{t('pag.teste')}</span>}
        {pedido.pixQrBase64
          ? <img className="lj-qr" src={`data:image/png;base64,${pedido.pixQrBase64}`} alt={t('pag.qrAlt')} />
          : <p className="muted">{t('pag.semQr')}</p>}
        {pedido.pixCopiaCola && (
          <div className="lj-copia">
            <input readOnly value={pedido.pixCopiaCola} aria-label={t('pag.copiaCola')} onFocus={e => e.currentTarget.select()} />
            <button type="button" className="btn btn--tiny" onClick={() => void copiar()}>{copiou ? t('pag.copiado') : t('pag.copiar')}</button>
          </div>
        )}
        <p className="lj-espera"><i />{t('pag.aguardando')}{relogio ? ` · ${relogio}` : ''}</p>
        <p>{t('pag.fecharNaoCancela')}</p>
        {erro && <p className="notice" role="alert">{erro}</p>}
        {confirmarCancelar ? (
          <div className="lj-caixa__acoes">
            <button type="button" className="btn btn--ghost" disabled={busy} onClick={() => setConfirmarCancelar(false)}>{t('pag.manter')}</button>
            <button type="button" className="btn btn--primary" disabled={busy} onClick={() => void cancelar()}>{t('pag.simCancelar')}</button>
          </div>
        ) : (
          <div className="lj-caixa__acoes">
            <button type="button" className="btn btn--ghost" disabled={busy || pedido.estado !== 'aguardando'} onClick={() => setConfirmarCancelar(true)}>{t('pag.cancelar')}</button>
            <button type="button" className="btn btn--primary" onClick={onFechar}>{t('comum.fechar')}</button>
          </div>
        )}
      </>
    );
  } else {
    const chave = `pag.estado.${pedido.estado}`;
    const recusado = pedido.estado === 'recusado';
    miolo = (
      <>
        <h3>{existe(chave) ? t(chave) : pedido.estado}</h3>
        <p>{recusado ? motivoDaRecusa(pedido.detalhe) : existe(`${chave}Dica`) ? t(`${chave}Dica`) : ''}</p>
        {recusado && <p className="lj-aviso">{t('pag.recusaNadaCobrado')}</p>}
        <button type="button" className="btn btn--primary" onClick={onFechar}>{t('comum.fechar')}</button>
      </>
    );
  }

  return (
    <div className="lj-modal" onClick={e => { if (e.target === e.currentTarget) onFechar(); }}>
      <div className="lj-caixa" role="dialog" aria-modal="true" aria-live="polite" ref={caixa}>{miolo}</div>
    </div>
  );
}
